import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PiPlusBold, PiTrashBold } from 'react-icons/pi';

import { fetchCustomerOrderDetail } from '#/api/crmMgmt/customerOrder/fetchDetail';
import { ROUTES } from '#/config/routes';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import { Checkbox } from '#/modules/commonComponents/checkbox';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import { getCustomerOrderApiErrorMessage } from '#/modules/crmMgmt/customerOrder/helper/apiError';
import {
  getCustomerOrderLineRemainingQty,
} from '#/modules/crmMgmt/customerOrder/helper/mapCustomerOrderMeta';
import { formatDisplayQty } from '#/utils/formatNumber';
import {
  createSalesOrdersFromCustomerOrderDrafts,
  getCreateSalesOrderFromDraftErrorMessage,
} from '#/modules/crmMgmt/salesOrder/helper/createSalesOrderFromCustomerOrderDraft';
import {
  mapCustomerOrderDetailToSalesOrderCreateDraft,
  mapCustomerOrderLineToSalesOrderCreateLine,
  mapCustomerOrderManualLineToSalesOrderCreateManualLine,
} from '#/modules/crmMgmt/salesOrder/helper/mapCustomerOrderDetailToSalesOrderCreateDraft';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import {
  customerOrderDetailQueryKey,
  customerOrderListQueryKeyPrefix,
  salesOrderDetailQueryKey,
  salesOrderListQueryKeyPrefix,
} from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

const LINE_GRID_COLS = cn(
  '@3xl/form-section:grid-cols-[2.25rem_minmax(10rem,1fr)_minmax(0,8rem)_minmax(0,12rem)]',
);

const LINE_HEADER_CLASS = cn(
  'sticky top-0 z-10 hidden w-full gap-3 border-b border-gray-300 bg-gray-100 px-3 py-2',
  'text-xs font-medium text-gray-600',
  '@3xl/form-section:grid',
  LINE_GRID_COLS,
);

const LINE_ROW_CLASS = cn(
  'grid w-full grid-cols-1 gap-3 px-3 py-3',
  '@3xl/form-section:items-center',
  LINE_GRID_COLS,
);

function defaultScheduledQty(line) {
  const remaining = getCustomerOrderLineRemainingQty(line);
  if (remaining == null || remaining === '') {
    return '';
  }
  return String(remaining);
}

function buildLineDrafts(lines) {
  return (lines ?? [])
    .filter((line) => line?.id)
    .map((line) => ({
      customerOrderLineId: line.id,
      selected: true,
      scheduledQty: defaultScheduledQty(line),
    }));
}

function buildManualLineDrafts(manualLines) {
  return (manualLines ?? [])
    .filter((line) => line?.id)
    .map((line) => ({
      customerOrderManualLineId: line.id,
      selected: true,
      scheduledQty: defaultScheduledQty(line),
    }));
}

function buildCreateDraftItem({
  detail,
  group,
  collectedLines,
  collectedManualLines,
  groupIndex,
}) {
  const header = mapCustomerOrderDetailToSalesOrderCreateDraft(detail);
  return {
    groupKey: group.key,
    groupNumber: groupIndex + 1,
    ...header,
    plannedShipDate: group.plannedShipDate,
    remark: group.remark.trim() || null,
    lines: collectedLines.map((row) => {
      const coLine = (detail.lines ?? []).find(
        (line) => line?.id === row.customerOrderLineId,
      );
      const draftLine = mapCustomerOrderLineToSalesOrderCreateLine(coLine) ?? {
        customerOrderLineId: row.customerOrderLineId,
        productId: null,
        qty: row.scheduledQty,
        unitPrice: '',
        discountRate: '',
        remark: '',
        invAllocations: [],
        extraItems: [],
      };
      return {
        ...draftLine,
        qty: row.scheduledQty,
        remark: row.remark ?? draftLine.remark ?? null,
      };
    }),
    manualLines: collectedManualLines.map((row) => {
      const coLine = (detail.manualLines ?? []).find(
        (line) => line?.id === row.customerOrderManualLineId,
      );
      const draftLine = mapCustomerOrderManualLineToSalesOrderCreateManualLine(
        coLine,
      ) ?? {
        customerOrderManualLineId: row.customerOrderManualLineId,
        itemName: '',
        qty: row.scheduledQty,
        unitName: '',
        unitPrice: '',
        remark: '',
      };
      return {
        ...draftLine,
        qty: row.scheduledQty,
        remark: row.remark ?? draftLine.remark ?? null,
      };
    }),
  };
}

function createScheduleGroup(lines, manualLines) {
  return {
    key: `g-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    plannedShipDate: '',
    remark: '',
    lineDrafts: buildLineDrafts(lines),
    manualLineDrafts: buildManualLineDrafts(manualLines),
  };
}

function collectScheduleFormMessages(plannedShipDate, lineDrafts, manualLineDrafts) {
  const messages = [];
  if (!plannedShipDate) {
    messages.push('請填寫預交日');
  }

  const selectedRows = lineDrafts.filter((row) => row.selected);
  const selectedManualRows = (manualLineDrafts ?? []).filter((row) => row.selected);
  if (selectedRows.length === 0 && selectedManualRows.length === 0) {
    messages.push('請至少勾選一筆產品或其他產品');
  }

  const lines = [];
  lineDrafts.forEach((row, index) => {
    if (!row.selected) {
      return;
    }
    const qty = Number(row.scheduledQty);
    if (
      row.scheduledQty === ''
      || row.scheduledQty == null
      || Number.isNaN(qty)
      || qty <= 0
    ) {
      messages.push(`產品排程量須為正數（第 ${index + 1} 列）`);
      return;
    }
    lines.push({
      customerOrderLineId: row.customerOrderLineId,
      scheduledQty: qty,
      remark: null,
    });
  });

  const manualLines = [];
  (manualLineDrafts ?? []).forEach((row, index) => {
    if (!row.selected) {
      return;
    }
    const qty = Number(row.scheduledQty);
    if (
      row.scheduledQty === ''
      || row.scheduledQty == null
      || Number.isNaN(qty)
      || qty <= 0
    ) {
      messages.push(`其他產品排程量須為正數（第 ${index + 1} 列）`);
      return;
    }
    manualLines.push({
      customerOrderManualLineId: row.customerOrderManualLineId,
      scheduledQty: qty,
      remark: null,
    });
  });

  if (messages.length === 0 && lines.length === 0 && manualLines.length === 0) {
    messages.push('請至少填一筆排程量');
  }

  return { messages, lines, manualLines };
}

function patchLineDraft(prev, customerOrderLineId, patch) {
  return prev.map((row) => (
    row.customerOrderLineId === customerOrderLineId
      ? { ...row, ...patch }
      : row
  ));
}

function patchGroup(prev, groupKey, patch) {
  return prev.map((group) => (
    group.key === groupKey ? { ...group, ...patch } : group
  ));
}

function patchGroupLine(prev, groupKey, customerOrderLineId, patch) {
  return prev.map((group) => {
    if (group.key !== groupKey) {
      return group;
    }
    return {
      ...group,
      lineDrafts: patchLineDraft(group.lineDrafts, customerOrderLineId, patch),
    };
  });
}

function patchManualLineDraft(prev, customerOrderManualLineId, patch) {
  return prev.map((row) => (
    row.customerOrderManualLineId === customerOrderManualLineId
      ? { ...row, ...patch }
      : row
  ));
}

function patchGroupManualLine(prev, groupKey, customerOrderManualLineId, patch) {
  return prev.map((group) => {
    if (group.key !== groupKey) {
      return group;
    }
    return {
      ...group,
      manualLineDrafts: patchManualLineDraft(
        group.manualLineDrafts ?? [],
        customerOrderManualLineId,
        patch,
      ),
    };
  });
}

function sumPendingScheduledQtyForLine(groups, createdGroupKeys, customerOrderLineId) {
  let sum = 0;
  groups.forEach((group) => {
    if (createdGroupKeys.includes(group.key)) {
      return;
    }
    (group.lineDrafts ?? []).forEach((row) => {
      if (!row.selected || row.customerOrderLineId !== customerOrderLineId) {
        return;
      }
      const qty = Number(row.scheduledQty);
      if (Number.isNaN(qty) || qty <= 0) {
        return;
      }
      sum += qty;
    });
  });
  return sum;
}

function sumPendingScheduledQtyForManualLine(
  groups,
  createdGroupKeys,
  customerOrderManualLineId,
) {
  let sum = 0;
  groups.forEach((group) => {
    if (createdGroupKeys.includes(group.key)) {
      return;
    }
    (group.manualLineDrafts ?? []).forEach((row) => {
      if (
        !row.selected
        || row.customerOrderManualLineId !== customerOrderManualLineId
      ) {
        return;
      }
      const qty = Number(row.scheduledQty);
      if (Number.isNaN(qty) || qty <= 0) {
        return;
      }
      sum += qty;
    });
  });
  return sum;
}

function isLineSchedulable(line) {
  if (!line?.id) {
    return false;
  }
  const remaining = Number(getCustomerOrderLineRemainingQty(line));
  if (Number.isFinite(remaining)) {
    return remaining > 0;
  }
  return true;
}

function hasSchedulableOrderContent(detail) {
  const hasProduct = (detail?.lines ?? []).some(isLineSchedulable);
  const hasManual = (detail?.manualLines ?? []).some(isLineSchedulable);
  return hasProduct || hasManual;
}

function isOverRemaining(remainingQty, allocated) {
  const remaining = Number(remainingQty);
  if (remainingQty === '' || remainingQty == null || Number.isNaN(remaining)) {
    return false;
  }
  return allocated > remaining;
}

const ScheduleGroupCard = ({
  group,
  groupIndex,
  orderLinesById,
  manualLinesById,
  allocatedByLineId,
  allocatedByManualLineId,
  canRemove,
  isCreated,
  isPending,
  onChange,
  onLineChange,
  onManualLineChange,
  onRemove,
}) => {
  const hasDraftLines = group.lineDrafts.length > 0;
  const hasManualDraftLines = (group.manualLineDrafts ?? []).length > 0;

  return (
    <li className="bg-panel">
      <div className="rounded border border-gray-400/65 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="form-subsection__title mb-0!">
            {`預交日 #${groupIndex + 1}`}
            {isCreated ? (
              <span className="ml-2 text-xs font-medium text-emerald-700">已建立</span>
            ) : null}
          </h3>
          {canRemove ? (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
              disabled={isPending}
              onClick={onRemove}
            >
              <PiTrashBold className="size-4" />
              移除此日
            </Button>
          ) : null}
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3">
          <FloatingDateInput
            id={`create-so-schedule-${group.key}-planned-ship-date`}
            label="預交日"
            value={group.plannedShipDate}
            onChange={(value) => onChange({ plannedShipDate: value })}
            placeholder="西元年/月/日"
            disabled={isPending || isCreated}
          />
          <FloatingTextarea
            id={`create-so-schedule-${group.key}-remark`}
            label="備註"
            value={group.remark}
            onChange={(event) => onChange({ remark: event.target.value })}
            rows={2}
            disabled={isPending || isCreated}
          />
        </div>

        <div className="mt-4">
          <h3 className="form-subsection__title mb-2">產品明細</h3>
          {!hasDraftLines ? (
            <p className="text-sm text-slate-600">本單尚無產品明細。</p>
          ) : (
            <div
              className={cn(
                'overflow-x-auto',
                '@3xl/form-section:rounded-md @3xl/form-section:border',
                '@3xl/form-section:border-gray-300',
              )}
            >
              <div className={LINE_HEADER_CLASS} aria-hidden>
                <span>納入</span>
                <span>產品</span>
                <span>剩餘</span>
                <span>排程量</span>
              </div>
              <ul className="m-0 list-none divide-y divide-gray-400/65 p-0">
                {group.lineDrafts.map((row, index) => {
                  const line = orderLinesById.get(row.customerOrderLineId);
                  const product = line?.product;
                  const remainingQty = getCustomerOrderLineRemainingQty(line);
                  const allocated = allocatedByLineId.get(row.customerOrderLineId) ?? 0;
                  const overRemaining = isOverRemaining(remainingQty, allocated);
                  const qtyDisabled = isPending || isCreated || !row.selected;

                  return (
                    <li key={row.customerOrderLineId} className={LINE_ROW_CLASS}>
                      <div className="flex min-w-0 items-center gap-2">
                        <p className="@3xl/form-section:hidden text-xs text-gray-500">
                          納入
                        </p>
                        <Checkbox
                          checked={row.selected}
                          disabled={isPending || isCreated}
                          aria-label={`第 ${groupIndex + 1} 組納入產品第 ${index + 1} 列`}
                          onCheckedChange={(checked) => onLineChange(
                            row.customerOrderLineId,
                            { selected: checked === true },
                          )}
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="@3xl/form-section:hidden text-xs text-gray-500">
                          產品
                        </p>
                        <MaterialNameStack
                          name={product?.name}
                          code={product?.productNo}
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="@3xl/form-section:hidden text-xs text-gray-500">
                          剩餘
                        </p>
                        <p className="text-sm tabular-nums text-gray-700">
                          {formatDisplayQty(remainingQty)}
                        </p>
                        {allocated > 0 ? (
                          <p
                            className={cn(
                              'text-xs tabular-nums',
                              overRemaining ? 'text-red-600' : 'text-muted-foreground',
                            )}
                          >
                            本表
                            {' '}
                            {formatDisplayQty(allocated)}
                          </p>
                        ) : null}
                      </div>
                      <div className="min-w-0">
                        <FloatingInput
                          id={`create-so-schedule-qty-${group.key}-${row.customerOrderLineId}`}
                          label="排程量"
                          type="number"
                          min={0}
                          step="any"
                          value={row.scheduledQty}
                          onChange={(event) => onLineChange(
                            row.customerOrderLineId,
                            { scheduledQty: event.target.value },
                          )}
                          containerClassName="mb-0! w-full"
                          suppressInlineError
                          readOnly={qtyDisabled}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <div className="mt-4">
          <h3 className="form-subsection__title mb-2">其他產品</h3>
          {!hasManualDraftLines ? (
            <p className="text-sm text-slate-600">本單尚無其他產品。</p>
          ) : (
            <div
              className={cn(
                'overflow-x-auto',
                '@3xl/form-section:rounded-md @3xl/form-section:border',
                '@3xl/form-section:border-gray-300',
              )}
            >
              <div className={LINE_HEADER_CLASS} aria-hidden>
                <span>納入</span>
                <span>品名</span>
                <span>剩餘</span>
                <span>排程量</span>
              </div>
              <ul className="m-0 list-none divide-y divide-gray-400/65 p-0">
                {(group.manualLineDrafts ?? []).map((row, index) => {
                  const line = manualLinesById.get(row.customerOrderManualLineId);
                  const remainingQty = getCustomerOrderLineRemainingQty(line);
                  const allocated = allocatedByManualLineId.get(
                    row.customerOrderManualLineId,
                  ) ?? 0;
                  const overRemaining = isOverRemaining(remainingQty, allocated);
                  const qtyDisabled = isPending || isCreated || !row.selected;
                  const itemLabel = [
                    line?.itemName,
                    line?.unitName ? `（${line.unitName}）` : '',
                  ].filter(Boolean).join('') || '—';

                  return (
                    <li
                      key={row.customerOrderManualLineId}
                      className={LINE_ROW_CLASS}
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <p className="@3xl/form-section:hidden text-xs text-gray-500">
                          納入
                        </p>
                        <Checkbox
                          checked={row.selected}
                          disabled={isPending || isCreated}
                          aria-label={
                            `第 ${groupIndex + 1} 組納入其他產品第 ${index + 1} 列`
                          }
                          onCheckedChange={(checked) => onManualLineChange(
                            row.customerOrderManualLineId,
                            { selected: checked === true },
                          )}
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="@3xl/form-section:hidden text-xs text-gray-500">
                          品名
                        </p>
                        <p className="text-sm text-gray-900 wrap-break-word">
                          {itemLabel}
                        </p>
                      </div>
                      <div className="min-w-0">
                        <p className="@3xl/form-section:hidden text-xs text-gray-500">
                          剩餘
                        </p>
                        <p className="text-sm tabular-nums text-gray-700">
                          {formatDisplayQty(remainingQty)}
                        </p>
                        {allocated > 0 ? (
                          <p
                            className={cn(
                              'text-xs tabular-nums',
                              overRemaining ? 'text-red-600' : 'text-muted-foreground',
                            )}
                          >
                            本表
                            {' '}
                            {formatDisplayQty(allocated)}
                          </p>
                        ) : null}
                      </div>
                      <div className="min-w-0">
                        <FloatingInput
                          id={
                            `create-so-schedule-manual-qty-${group.key}-`
                            + `${row.customerOrderManualLineId}`
                          }
                          label="排程量"
                          type="number"
                          min={0}
                          step="any"
                          value={row.scheduledQty}
                          onChange={(event) => onManualLineChange(
                            row.customerOrderManualLineId,
                            { scheduledQty: event.target.value },
                          )}
                          containerClassName="mb-0! w-full"
                          suppressInlineError
                          readOnly={qtyDisabled}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
    </li>
  );
};

ScheduleGroupCard.propTypes = {
  group: PropTypes.shape({
    key: PropTypes.string.isRequired,
    plannedShipDate: PropTypes.string,
    remark: PropTypes.string,
    lineDrafts: PropTypes.arrayOf(PropTypes.object).isRequired,
    manualLineDrafts: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  groupIndex: PropTypes.number.isRequired,
  orderLinesById: PropTypes.instanceOf(Map).isRequired,
  manualLinesById: PropTypes.instanceOf(Map).isRequired,
  allocatedByLineId: PropTypes.instanceOf(Map).isRequired,
  allocatedByManualLineId: PropTypes.instanceOf(Map).isRequired,
  canRemove: PropTypes.bool.isRequired,
  isCreated: PropTypes.bool.isRequired,
  isPending: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onLineChange: PropTypes.func.isRequired,
  onManualLineChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

const ScheduleDraftForm = ({ customerOrderId, detail }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [groups, setGroups] = useState(() => [
    createScheduleGroup(detail.lines, detail.manualLines),
  ]);
  const [createdGroupKeys, setCreatedGroupKeys] = useState([]);
  const [progress, setProgress] = useState(null);
  const {
    customMessages,
    isSubmitAttempted,
    markSubmitAttempted,
    setCustomMessages,
  } = useFormValidationSummary();

  const orderLinesById = useMemo(() => {
    const map = new Map();
    (detail.lines ?? []).forEach((line) => {
      if (line?.id) {
        map.set(line.id, line);
      }
    });
    return map;
  }, [detail.lines]);

  const manualLinesById = useMemo(() => {
    const map = new Map();
    (detail.manualLines ?? []).forEach((line) => {
      if (line?.id) {
        map.set(line.id, line);
      }
    });
    return map;
  }, [detail.manualLines]);

  const allocatedByLineId = useMemo(() => {
    const map = new Map();
    orderLinesById.forEach((_, lineId) => {
      map.set(
        lineId,
        sumPendingScheduledQtyForLine(groups, createdGroupKeys, lineId),
      );
    });
    return map;
  }, [groups, createdGroupKeys, orderLinesById]);

  const allocatedByManualLineId = useMemo(() => {
    const map = new Map();
    manualLinesById.forEach((_, lineId) => {
      map.set(
        lineId,
        sumPendingScheduledQtyForManualLine(groups, createdGroupKeys, lineId),
      );
    });
    return map;
  }, [groups, createdGroupKeys, manualLinesById]);

  const mutation = useMutation({
    mutationFn: (items) => createSalesOrdersFromCustomerOrderDrafts(
      customerOrderId,
      items,
      {
        onGroupStart: (index, total) => {
          setProgress({ current: index + 1, total });
        },
        onGroupSuccess: (groupKey) => {
          setCreatedGroupKeys((prev) => (
            prev.includes(groupKey) ? prev : [...prev, groupKey]
          ));
        },
      },
    ),
  });

  const isPending = mutation.isPending;
  const hasOrderLines = hasSchedulableOrderContent(detail);
  const pendingGroups = groups.filter((group) => !createdGroupKeys.includes(group.key));

  async function invalidateQueries(salesOrderIds = []) {
    await queryClient.invalidateQueries({
      queryKey: customerOrderListQueryKeyPrefix(),
    });
    await queryClient.invalidateQueries({
      queryKey: customerOrderDetailQueryKey(customerOrderId),
    });
    await queryClient.invalidateQueries({
      queryKey: salesOrderListQueryKeyPrefix(),
    });
    await Promise.all(salesOrderIds.map((id) => (
      queryClient.invalidateQueries({
        queryKey: salesOrderDetailQueryKey(id),
      })
    )));
  }

  function handleAddGroup() {
    if (isPending || !hasOrderLines) {
      return;
    }
    setGroups((prev) => [
      ...prev,
      createScheduleGroup(detail.lines, detail.manualLines),
    ]);
  }

  function handleRemoveGroup(groupKey) {
    if (isPending || createdGroupKeys.includes(groupKey)) {
      return;
    }
    setGroups((prev) => (prev.length <= 1 ? prev : prev.filter((group) => (
      group.key !== groupKey
    ))));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (isPending) {
      return;
    }

    markSubmitAttempted();
    const messages = [];
    const items = [];
    groups.forEach((group, groupIndex) => {
      if (createdGroupKeys.includes(group.key)) {
        return;
      }
      const collected = collectScheduleFormMessages(
        group.plannedShipDate,
        group.lineDrafts,
        group.manualLineDrafts,
      );
      collected.messages.forEach((message) => {
        messages.push(`第 ${groupIndex + 1} 組：${message}`);
      });
      if (collected.messages.length === 0) {
        items.push(buildCreateDraftItem({
          detail,
          group,
          collectedLines: collected.lines,
          collectedManualLines: collected.manualLines,
          groupIndex,
        }));
      }
    });

    if (messages.length > 0) {
      setCustomMessages(messages);
      return;
    }
    if (items.length === 0) {
      setCustomMessages(['沒有待建立的預交日']);
      return;
    }

    setCustomMessages([]);
    try {
      const results = await mutation.mutateAsync(items);
      const salesOrderIds = results.map((result) => result?.data?.id).filter(Boolean);
      await invalidateQueries(salesOrderIds);

      const totalCreated = createdGroupKeys.length + results.length;
      if (results.length === 1 && totalCreated === 1) {
        toast.success(results[0]?.message || '銷貨單已建立');
        navigate(ROUTES.salesOrderEdit(salesOrderIds[0]), { replace: true });
        return;
      }
      toast.success(`已建立 ${totalCreated} 張銷貨單`);
      navigate(ROUTES.customerOrderDetail(customerOrderId), { replace: true });
    } catch (error) {
      await invalidateQueries();
      toast.error(getCreateSalesOrderFromDraftErrorMessage(error));
    } finally {
      setProgress(null);
    }
  }

  let submitLabel = '建立銷貨單';
  if (isPending && progress) {
    submitLabel = `建立中（第 ${progress.current}／${progress.total} 組）…`;
  } else if (isPending) {
    submitLabel = '建立中…';
  } else if (pendingGroups.length > 1) {
    submitLabel = `建立 ${pendingGroups.length} 張銷貨單`;
  }

  return (
    <form
      className="sheet create_edit create_edit_form create_edit_form--page-scroll"
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="create_edit_form__body divide-y divide-gray-500">
        <section className="@container/form-section pb-6">
          <div className="form-section__header">
            <h2 className="form-block__title mb-0!">建立銷貨單</h2>
            <Button
              type="button"
              size="sm"
              disabled={isPending || !hasOrderLines}
              onClick={handleAddGroup}
            >
              <PiPlusBold className="size-3.5" />
              新增預交日
            </Button>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            每一個預交日會各開一張草稿銷貨單，並帶入客訂的地址與稅務設定。
            畫面上的剩餘量僅供參考，實際能否建立以儲存結果為準。
          </p>
          <FormValidationSummary
            show={isSubmitAttempted}
            customMessages={customMessages}
          />

          <ul
            className={cn(
              'mt-2.5 p-0 list-none',
              'grid gap-2 grid-cols-1 @xl/sheet:grid-cols-2',
            )}
          >
            {groups.map((group, groupIndex) => {
              const isCreated = createdGroupKeys.includes(group.key);
              const canRemove = groups.length > 1 && !isCreated;
              return (
                <ScheduleGroupCard
                  key={group.key}
                  group={group}
                  groupIndex={groupIndex}
                  orderLinesById={orderLinesById}
                  manualLinesById={manualLinesById}
                  allocatedByLineId={allocatedByLineId}
                  allocatedByManualLineId={allocatedByManualLineId}
                  canRemove={canRemove}
                  isCreated={isCreated}
                  isPending={isPending}
                  onChange={(patch) => {
                    setGroups((prev) => patchGroup(prev, group.key, patch));
                  }}
                  onLineChange={(lineId, patch) => {
                    setGroups((prev) => patchGroupLine(prev, group.key, lineId, patch));
                  }}
                  onManualLineChange={(lineId, patch) => {
                    setGroups((prev) => (
                      patchGroupManualLine(prev, group.key, lineId, patch)
                    ));
                  }}
                  onRemove={() => handleRemoveGroup(group.key)}
                />
              );
            })}
          </ul>
        </section>
      </div>

      <div className="create_edit_form__actions mt-4 flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" asChild>
          <Link to={ROUTES.customerOrderDetail(customerOrderId)}>取消</Link>
        </Button>
        <Button
          type="submit"
          disabled={isPending || !hasOrderLines || pendingGroups.length === 0}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};

ScheduleDraftForm.propTypes = {
  customerOrderId: PropTypes.string.isRequired,
  detail: PropTypes.shape({
    lines: PropTypes.arrayOf(PropTypes.object),
    manualLines: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
};

const CreateSalesOrderScheduleForm = ({ customerOrderId }) => {
  const detailQuery = useQuery({
    queryKey: customerOrderDetailQueryKey(customerOrderId),
    queryFn: () => fetchCustomerOrderDetail(customerOrderId),
    enabled: Boolean(customerOrderId),
  });

  if (!customerOrderId) {
    return null;
  }

  if (detailQuery.isLoading) {
    return <p className="text-sm text-slate-500">載入訂單明細中…</p>;
  }

  if (detailQuery.isError) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-destructive">
          {getCustomerOrderApiErrorMessage(detailQuery.error, '載入客戶訂單失敗')}
        </p>
        <Button type="button" variant="outline" asChild>
          <Link to={ROUTES.customerOrderDetail(customerOrderId)}>
            返回客戶訂單詳情
          </Link>
        </Button>
      </div>
    );
  }

  const detail = detailQuery.data?.data;
  if (!detail) {
    return (
      <p className="text-sm text-slate-600">找不到客戶訂單明細。</p>
    );
  }

  return (
    <ScheduleDraftForm
      customerOrderId={customerOrderId}
      detail={detail}
    />
  );
};

CreateSalesOrderScheduleForm.propTypes = {
  customerOrderId: PropTypes.string.isRequired,
};

export default CreateSalesOrderScheduleForm;
